import { Form, Formik } from 'formik'
import { useRouter } from 'next/router'
import React, { useRef, useEffect } from 'react'
import { useCouponsSettingsQuery } from '@public/hooks/useCouponsSettingsQuery'
import { Button } from 'components/Button'
import Icon from 'components/Icon'
import { Flex } from 'components/Layout'
import { MediaQueryWrapper } from 'components/MediaQueryWrapper'
import { Toast } from 'components/Toast'
import { CURRENCY_ISO_CODE, PAYMENT_OPTIONS } from 'constants/common'
import { ROUTE } from 'constants/routes'
import { withGA4Provider } from 'hoc/withGoogleAnalytics'
import { useCheckout } from 'hooks/useCheckout'
import { usePaymentRequest, getPaymentCallbacks } from 'hooks/usePaymentRequest'
import { useCurrency } from 'providers/currency'
import {
  useGoogleAnalytics,
  GA4_EVENTS,
  GA4_PURCHASE_TYPES,
  GA4_AREAS,
  GA4_PAGE_AREAS,
  GA4_ECOM_CUSTOM_EVENTS,
} from 'providers/googleAnalytics'
import { trackPurchasesToGA4 } from 'providers/googleAnalytics/utils/purchaseUtils'
import { useUserAuth } from 'providers/userAuth'
import { SPACE } from 'Theme'
import { getPaymentLayout } from '../components/PaymentLayout'
import { useGetCheckoutSummaryData } from '../hooks/useGetCheckoutSummaryData'
import { CouponCheckoutElements } from './CouponCheckoutElements'
import { CouponSummary } from './CouponSummary'
import { couponCheckoutValidationSchema } from './validationSchema'

const DEFAULT_AMOUNT = 300

const formatCheckoutInput = ({
  contactEmail,
  card,
  validAmount,
  isDifferentRecipient,
  buyerFullName,
  recipientEmail,
  recipientMessage,
  recipientFirstName,
  paymentMethodId,
  onStripePaymentConfirm,
  paymentMethod = PAYMENT_OPTIONS.CARD,
}) => ({
  email: contactEmail.trim(),
  paymentMethod,
  onStripePaymentConfirm,
  checkoutItems: [
    {
      quantity: 1,
      coupon: {
        amount: validAmount,
        ...(isDifferentRecipient &&
          recipientEmail && {
            differentRecipient: {
              recipientEmail: recipientEmail.trim(),
              recipientMessage: recipientMessage.trim(),
              recipientFirstName: recipientFirstName.trim(),
              buyerFullName: buyerFullName.trim(),
            },
          }),
      },
    },
  ],
  ...(card && { card }),
  ...(paymentMethodId && { paymentMethodId }),
})

const CouponCheckoutPage = () => {
  const { user, isClientSignedIn } = useUserAuth()
  const selectedCurrency = useCurrency()
  const router = useRouter()
  const { executeDataToDataLayer } = useGoogleAnalytics()

  const { couponsSettings, loading: isCouponsSettingsLoading } =
    useCouponsSettingsQuery()
  const {
    couponSummary,
    isLoading: isCouponSummaryLoading,
    refetchPrice: priceRefetch,
  } = useGetCheckoutSummaryData(DEFAULT_AMOUNT)

  useEffect(() => {
    if (!isCouponSummaryLoading && couponSummary?.price) {
      executeDataToDataLayer({
        event: GA4_EVENTS.BEGIN_CHECKOUT,
        overrideData: {
          items: [couponSummary],
        },
      })

      executeDataToDataLayer({
        event: GA4_ECOM_CUSTOM_EVENTS.VIEW_CHECKOUT_GIFTCARD,
        includeStoredData: false,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCouponSummaryLoading])

  const [executeCheckout, { isLoading: isPurchaseLoading }] = useCheckout({
    onError: Toast.error,
    onSuccess: ({ checkoutResponse, paymentMethod }) => {
      trackPurchasesToGA4({
        checkoutResponse,
        paymentMethod,
        purchaseType: GA4_PURCHASE_TYPES.GIFT_CARD,
        executeDataToDataLayer,
        extra: { couponSummary },
      })
      router.push(ROUTE.COUPON_CHECKOUT_THANK_YOU_PAGE())
    },
  })

  const formRef = useRef(null)

  const [
    executePayment,
    { availablePaymentsOptions, isLoading: isLoadingPaymentRequest },
  ] = usePaymentRequest({
    amount: couponSummary.price,
    currency: CURRENCY_ISO_CODE.USD, // TODO: change to user's currency, when supported
    ...getPaymentCallbacks({
      ref: formRef,
      submit: executeCheckout,
      submitFormatter: formatCheckoutInput,
    }),
  })

  const isLoading = isPurchaseLoading || isCouponsSettingsLoading

  return (
    <Formik
      innerRef={formRef}
      validationSchema={couponCheckoutValidationSchema}
      enableReinitialize // if context is initialized later then formik
      initialValues={{
        amount: DEFAULT_AMOUNT,
        validAmount: DEFAULT_AMOUNT, // if amount is valid, then is set to validAmount
        isGuest: !user || !isClientSignedIn,
        card: null,
        isDifferentRecipient: false,
        buyerFullName: isClientSignedIn && user ? user.fullName : '',
        contactEmail: user?.contactEmail ?? '',
        recipientEmail: '',
        recipientMessage: '',
        recipientFirstName: '',
        paymentOption: availablePaymentsOptions[0] || PAYMENT_OPTIONS.CARD,
      }}
      validateOnChange={false}
      validateOnBlur
      onSubmit={(values) =>
        executeCheckout(
          formatCheckoutInput({
            ...values,
            paymentMethod: PAYMENT_OPTIONS.CARD,
          })
        )
      }
    >
      {({
        setFieldValue,
        setFieldError,
        isSubmitting: isPurchaseSubmitting,
        values,
        handleSubmit,
      }) => (
        <Form
          onSubmit={(event) => {
            // need to disable submit by enter (problem with stripe card payment elements)
            if (values.paymentOption === PAYMENT_OPTIONS.CARD) {
              event.preventDefault()
            }
          }}
        >
          <Flex
            flexDirection={{ MOBILE: 'column', TABLET: 'row' }}
            justifyContent="space-between"
          >
            <CouponCheckoutElements
              values={values}
              availablePaymentOptions={[
                ...availablePaymentsOptions,
                PAYMENT_OPTIONS.CARD,
              ]}
              setFieldValue={setFieldValue}
              handleSubmit={
                values.paymentOption !== PAYMENT_OPTIONS.CARD
                  ? executePayment
                  : handleSubmit
              }
              isPurchaseSubmitting={isPurchaseSubmitting}
              isPurchaseLoading={false}
              isLoading={isLoading || isPurchaseSubmitting}
              isLoadingOptions={isLoadingPaymentRequest}
              isClientSignedIn={user && isClientSignedIn}
              onChangeAmount={(amount) => {
                priceRefetch({
                  input: {
                    checkoutItems: [
                      {
                        quantity: 1,
                        coupon: { amount },
                      },
                    ],
                    currencyIsoCode: selectedCurrency.currencyIsoCode,
                  },
                }).then(
                  () => setFieldValue('validAmount', amount),
                  () =>
                    setFieldError(
                      'amount',
                      'Error ocurred. Please try typing your amount again'
                    )
                )
              }}
            />
            <CouponSummary
              isDiscountEnabled={couponsSettings.isDiscountEnabled}
              isLoading={isCouponSummaryLoading}
              couponSummary={couponSummary}
            />
            <MediaQueryWrapper
              mobile={
                <Button
                  mt={SPACE.PX_20}
                  isDisabled={isPurchaseSubmitting}
                  isLoading={isLoading || isPurchaseSubmitting}
                  icon={Icon.Lock}
                  onClick={
                    values.paymentOption !== PAYMENT_OPTIONS.CARD
                      ? executePayment
                      : handleSubmit
                  }
                >
                  Confirm and pay
                </Button>
              }
              tablet={null}
            />
          </Flex>
        </Form>
      )}
    </Formik>
  )
}

export const CouponCheckout = withGA4Provider({
  area: GA4_AREAS.GIFT_CARD,
  page_area: GA4_PAGE_AREAS.COUPON_CHECKOUT,
})(CouponCheckoutPage)

CouponCheckout.getLayout = getPaymentLayout
